import React, { useState, useContext } from 'react';
import { 
  Container, Typography, Box, Button, Dialog, 
  DialogActions, Table, TableBody, TableCell, 
  TableHead, TableRow, IconButton, Chip, CircularProgress, Alert, Tabs, Tab,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Grid, Card, CardContent, CardActions
} from '@mui/material';
import { AnimatedPaper, StyledTableContainer, StyledButton, tableRowHoverStyles, iconButtonStyles, StyledDialogTitle, StyledDialogContent } from '../components/StyledComponents';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useQuery, useMutation, gql } from '@apollo/client';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import AuthContext from '../contexts/AuthContext';
import SearchableSelect from '../components/SearchableSelect';

// GraphQL queries et mutations
const GET_ABSENCE_REQUESTS = gql`
  query GetAbsenceRequests($employeeId: ID, $status: String, $startDate: Date, $endDate: Date, $departmentId: ID) {
    absenceRequests(employeeId: $employeeId, status: $status, startDate: $startDate, endDate: $endDate, departmentId: $departmentId) {
      id
      employee {
        id
        firstName
        lastName
        email
      department {
        id
        name
      }
      function {
        id
        name
      }
      }
      absenceType {
        id
        code
        name
        description
      }
      startDate
      endDate
      durationDays
      reason
      status
      approvedBy {
        id
        firstName
        lastName
      }
      approvedAt
      rejectionReason
      documentUrl
      createdAt
      updatedAt
    }
  }
`;

const GET_ABSENCE_TYPES = gql`
  query GetAbsenceTypes {
    absenceTypes {
      id
      code
      name
      description
      isWorkingDay
    }
  }
`;

const GET_USERS = gql`
  query GetUsers($role: String, $departmentId: ID) {
    users(role: $role, departmentId: $departmentId) {
      id
      firstName
      lastName
      email
      role
      department {
        id
        name
      }
      function {
        id
        name
      }
    }
  }
`;

const GET_DEPARTMENTS = gql`
  query GetDepartments {
    departments {
      id
      name
    }
  }
`;

const GET_EMPLOYEE_PRESENCES = gql`
  query GetEmployeePresences($employeeId: ID, $departmentId: ID, $year: Int, $month: Int) {
    employeePresences(employeeId: $employeeId, departmentId: $departmentId, year: $year, month: $month) {
      id
      employee {
        id
        firstName
        lastName
        email
      }
      department {
        id
        name
      }
      function {
        id
        name
      }
      year
      month
      totalSickLeave
      totalVacation
      totalMission
      totalPaternity
      totalCasual
      totalCompassionate
      totalWorkFromHome
      totalCompOff
      totalHalfDay1
      totalHalfDay2
      createdAt
      updatedAt
    }
  }
`;

const CREATE_ABSENCE_REQUEST = gql`
  mutation CreateAbsenceRequest(
    $absenceTypeId: ID!
    $startDate: Date!
    $endDate: Date!
    $durationDays: Decimal!
    $reason: String!
    $employeeId: ID
  ) {
    createAbsenceRequest(
      absenceTypeId: $absenceTypeId
      startDate: $startDate
      endDate: $endDate
      durationDays: $durationDays
      reason: $reason
      employeeId: $employeeId
    ) {
      success
      message
      absenceRequest {
        id
        employee {
          id
          firstName
          lastName
        }
        absenceType {
          id
          name
        }
        startDate
        endDate
        status
      }
    }
  }
`;

const UPDATE_ABSENCE_REQUEST = gql`
  mutation UpdateAbsenceRequest(
    $id: ID!
    $status: String
    $rejectionReason: String
  ) {
    updateAbsenceRequest(
      id: $id
      status: $status
      rejectionReason: $rejectionReason
    ) {
      success
      message
      absenceRequest {
        id
        status
        approvedBy {
          id
          firstName
          lastName
        }
        approvedAt
        rejectionReason
      }
    }
  }
`;

const DELETE_ABSENCE_REQUEST = gql`
  mutation DeleteAbsenceRequest($id: ID!) {
    deleteAbsenceRequest(id: $id) {
      success
      message
    }
  }
`;

const UPLOAD_ABSENCE_DOCUMENT = gql`
  mutation UploadAbsenceDocument($id: ID!, $document: Upload!) {
    uploadAbsenceDocument(id: $id, document: $document) {
      success
      message
      absenceRequest {
        id
        documentUrl
      }
    }
  }
`;

const APPROVE_ABSENCE_REQUEST = gql`
  mutation ApproveAbsenceRequest($id: ID!, $approved: Boolean!, $rejectionReason: String) {
    approveAbsenceRequest(id: $id, approved: $approved, rejectionReason: $rejectionReason) {
      success
      message
      absenceRequest {
        id
        status
        approvedBy {
          id
          firstName
          lastName
        }
        approvedAt
        rejectionReason
      }
    }
  }
`;

function Absences() {
  const { user } = useContext(AuthContext);
  
  // État pour les onglets
  const [tabValue, setTabValue] = useState(0);
  
  // État pour la recherche et filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [absenceTypeFilter, setAbsenceTypeFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState(new Date().getMonth() + 1);
  
  // États pour les dialogues
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openValidateDialog, setOpenValidateDialog] = useState(false);
  
  // États pour les formulaires
  const [formData, setFormData] = useState({
    absenceTypeId: '',
    startDate: '',
    endDate: '',
    durationDays: '',
    reason: '',
    employeeId: ''
  });
  
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [validationComment, setValidationComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalDialog, setApprovalDialog] = useState(false);

  const parseDateOnly = (value) => {
    if (!value) return null;
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
  };

  const computeDurationDays = (startDate, endDate) => {
    const start = parseDateOnly(startDate);
    const end = parseDateOnly(endDate);
    if (!start || !end) return '';
    const diffMs = end - start;
    if (diffMs < 0) return '';
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  };

  // Queries
  const { data: absenceRequestsData, loading: absenceRequestsLoading, refetch: refetchAbsenceRequests } = useQuery(
    GET_ABSENCE_REQUESTS,
    {
      variables: {
        employeeId: (user?.role === 'ADMIN' || user?.role === 'RH' || user?.role === 'MANAGER') ? null : user?.id,
        status: statusFilter || null,
        startDate: null,
        endDate: null
      }
    }
  );

  // Requête spécifique pour les demandes du département (onglet 2)
  const { data: departmentRequestsData, loading: departmentRequestsLoading, refetch: refetchDepartmentRequests } = useQuery(
    GET_ABSENCE_REQUESTS,
    {
      variables: {
        employeeId: null, // Toutes les demandes
        status: statusFilter || null,
        startDate: null,
        endDate: null,
        departmentId: (user?.role === 'RH' || user?.role === 'HOST') ? user?.department?.id : null
      },
      skip: !['ADMIN', 'RH', 'HOST'].includes(user?.role) || tabValue !== 1,
      fetchPolicy: 'cache-and-network'
    }
  );

  const { data: absenceTypesData, loading: absenceTypesLoading } = useQuery(GET_ABSENCE_TYPES);
  const { data: usersData, loading: usersLoading } = useQuery(GET_USERS, {
    variables: { role: null },
    skip: !['ADMIN'].includes(user?.role)
  });
  
  const { data: departmentsData, loading: departmentsLoading } = useQuery(GET_DEPARTMENTS);
  
  const { data: employeePresencesData, loading: employeePresencesLoading } = useQuery(
    GET_EMPLOYEE_PRESENCES,
    {
      variables: {
        employeeId: (user?.role === 'ADMIN' || user?.role === 'RH' || user?.role === 'MANAGER') ? null : user?.id,
        departmentId: (user?.role === 'MANAGER' || user?.role === 'RH') ? user?.department?.id : null,
        year: yearFilter,
        month: monthFilter
      }
    }
  );

  // Requête spécifique pour les managers, RH et hôtes (utilisateurs de leur département)
  const { data: departmentUsersData, loading: departmentUsersLoading } = useQuery(GET_USERS, {
    variables: { departmentId: user?.department?.id },
    skip: !['MANAGER', 'RH', 'HOST'].includes(user?.role)
  });

  // Mutations
  const [createAbsenceRequest, { loading: createLoading }] = useMutation(CREATE_ABSENCE_REQUEST);
  const [updateAbsenceRequest, { loading: updateLoading }] = useMutation(UPDATE_ABSENCE_REQUEST);
  const [deleteAbsenceRequest, { loading: deleteLoading }] = useMutation(DELETE_ABSENCE_REQUEST);
  const [approveAbsenceRequest, { loading: approvalLoading }] = useMutation(APPROVE_ABSENCE_REQUEST);
  const [uploadAbsenceDocument, { loading: uploadLoading }] = useMutation(UPLOAD_ABSENCE_DOCUMENT);

  // Filtrer les demandes d'absence
  const getRequestsData = () => {
    if (tabValue === 1 && ['ADMIN', 'RH', 'HOST'].includes(user?.role)) {
      return departmentRequestsData?.absenceRequests || [];
    }
    return absenceRequestsData?.absenceRequests || [];
  };

  const filteredRequests = getRequestsData().filter(request => {
    // Vérification de sécurité pour éviter les erreurs
    if (!request || !request.employee || !request.absenceType) {
      return false;
    }

    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
      request.employee.firstName.toLowerCase().includes(searchLower) ||
      request.employee.lastName.toLowerCase().includes(searchLower) ||
      request.employee.email.toLowerCase().includes(searchLower) ||
      (request.employee.department?.name || '').toLowerCase().includes(searchLower) ||
      request.absenceType.name.toLowerCase().includes(searchLower) ||
      request.reason.toLowerCase().includes(searchLower);

    const matchesStatus = !statusFilter || request.status === statusFilter;
    const matchesAbsenceType = !absenceTypeFilter || request.absenceType.id === absenceTypeFilter;
    const matchesDepartment = !departmentFilter || (request.employee.department && request.employee.department.id === departmentFilter);

    return matchesSearch && matchesStatus && matchesAbsenceType && matchesDepartment;
  });

  // Gestionnaires d'événements
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenCreateDialog = () => {
    const initialFormData = {
      absenceTypeId: '',
      startDate: '',
      endDate: '',
      durationDays: '',
      reason: '',
      employeeId: (user?.role === 'ADMIN' || user?.role === 'RH' || user?.role === 'MANAGER' || user?.role === 'HOST' || user?.role === 'EMPLOYEE') ? user?.id : ''
    };
    setFormData(initialFormData);
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    // Réinitialiser le formulaire
    setFormData({
      absenceTypeId: '',
      startDate: '',
      endDate: '',
      durationDays: '',
      reason: '',
      employeeId: ''
    });
    setSelectedDocument(null);
  };

  const handleOpenEditDialog = (request) => {
    setSelectedRequest(request);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedRequest(null);
  };

  const handleOpenDetails = (request) => {
    setSelectedRequest(request);
    setOpenDetailsDialog(true);
  };

  const handleCloseDetails = () => {
    setOpenDetailsDialog(false);
    setSelectedRequest(null);
  };

  const handleOpenDeleteDialog = (request) => {
    setSelectedRequest(request);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedRequest(null);
  };

  const handleOpenValidateDialog = (request) => {
    setSelectedRequest(request);
    setValidationComment('');
    setRejectionReason('');
    setOpenValidateDialog(true);
  };

  const handleCloseValidateDialog = () => {
    setOpenValidateDialog(false);
    setSelectedRequest(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const computedDuration = computeDurationDays(formData.startDate, formData.endDate);
      const result = await createAbsenceRequest({
        variables: {
          absenceTypeId: formData.absenceTypeId,
          startDate: formData.startDate,
          endDate: formData.endDate,
          durationDays: computedDuration !== '' ? computedDuration : parseFloat(formData.durationDays),
          reason: formData.reason,
          employeeId: (user?.role === 'ADMIN' || user?.role === 'RH' || user?.role === 'MANAGER' || user?.role === 'HOST' || user?.role === 'EMPLOYEE') ? formData.employeeId : user?.id
        }
      });

      if (result.data.createAbsenceRequest.success) {
        const absenceId = result.data.createAbsenceRequest.absenceRequest.id;
        
        // Upload document if selected
        if (selectedDocument) {
          try {
            await uploadAbsenceDocument({
              variables: {
                id: absenceId,
                document: selectedDocument
              }
            });
          } catch (uploadError) {
            console.error('Erreur lors de l\'upload du document:', uploadError);
            // alert('Demande créée mais erreur lors de l\'upload du document');
          }
        }
        
        handleCloseCreateDialog();
        setSelectedDocument(null);
        refetchAbsenceRequests();
        // Refetch les demandes du département si on est dans l'onglet approprié
        if (['ADMIN', 'RH', 'HOST'].includes(user?.role)) {
          refetchDepartmentRequests();
        }
        // alert('Demande d\'absence créée avec succès');
    } else {
        // alert('Erreur: ' + result.data.createAbsenceRequest.message);
      }
    } catch (error) {
      // alert('Erreur lors de la création: ' + error.message);
    }
  };

  const handleDelete = async () => {
    try {
      const result = await deleteAbsenceRequest({
        variables: { id: selectedRequest.id }
      });

      if (result.data.deleteAbsenceRequest.success) {
        handleCloseDeleteDialog();
        refetchAbsenceRequests();
        // Refetch les demandes du département si on est dans l'onglet approprié
        if (['ADMIN', 'RH', 'HOST'].includes(user?.role)) {
          refetchDepartmentRequests();
        }
        //  alert('Demande d\'absence supprimée avec succès');
      } else {
        // alert('Erreur: ' + result.data.deleteAbsenceRequest.message);
      }
    } catch (error) {
      // alert('Erreur lors de la suppression: ' + error.message);
    }
  };

  const handleValidate = async (status) => {
    try {
      const variables = { id: selectedRequest.id, status };
      
      if (status === 'REJECTED' && !rejectionReason) {
        alert('Veuillez fournir un motif de rejet');
        return;
      }
      
      if (status === 'REJECTED') {
        variables.rejectionReason = rejectionReason;
      }

      const result = await updateAbsenceRequest({ variables });

      if (result.data.updateAbsenceRequest.success) {
        handleCloseValidateDialog();
        refetchAbsenceRequests();
        // Refetch les demandes du département si on est dans l'onglet approprié
        if (['ADMIN', 'RH', 'HOST'].includes(user?.role)) {
          refetchDepartmentRequests();
        }
        //  alert(`Demande d'absence ${status === 'APPROVED' ? 'approuvée' : 'rejetée'} avec succès`);
      } else {
        // alert('Erreur: ' + result.data.updateAbsenceRequest.message);
      }
    } catch (error) {
      // alert('Erreur lors de la validation: ' + error.message);
    }
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'PENDING': 'En attente',
      'APPROVED': 'Approuvée',
      'REJECTED': 'Rejetée',
      'CANCELLED': 'Annulée'
    };
    return statusLabels[status] || status;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'PENDING': 'warning',
      'APPROVED': 'success',
      'REJECTED': 'error',
      'CANCELLED': 'default'
    };
    return statusColors[status] || 'default';
  };

  const getAbsenceTypeLabel = (code) => {
    const typeLabels = {
      'S': 'Congé Maladie',
      'V': 'Vacances',
      'M': 'Mission',
      'P': 'Congé Paternité',
      'C': 'Congé Occasionnel',
      'Co': 'Congé Compassion',
      'W': 'Télétravail',
      'O': 'Compensation',
      'H1': 'Demi-journée 1',
      'H2': 'Demi-journée 2'
    };
    return typeLabels[code] || code;
  };

  const handleApproval = async (approved) => {
    if (!selectedRequest) return;

    try {
      await approveAbsenceRequest({
        variables: {
          id: selectedRequest.id,
          approved,
          rejectionReason: approved ? null : rejectionReason
        }
      });
      
      setOpenValidateDialog(false);
      setSelectedRequest(null);
      setRejectionReason('');
      refetchAbsenceRequests();
      
      // Refetch les demandes du département si on est dans l'onglet approprié
      if (['ADMIN', 'RH', 'HOST'].includes(user?.role)) {
        refetchDepartmentRequests();
      }
    } catch (error) {
      console.error('Erreur lors de l\'approbation:', error);
    }
  };

  // Fonction pour générer les options du sélecteur d'employé sous forme d'objets
  const getEmployeeOptionsList = () => {
    const options = [];
    
    // Option pour créer une demande pour soi-même
    if (user?.id) {
      options.push({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        label: `Moi-même (${user.firstName} ${user.lastName})`
      });
    }
    
    // Autres employés pour les rôles autorisés
    if (['ADMIN', 'RH', 'MANAGER', 'HOST'].includes(user?.role)) {
      let employeeList = [];
      
      if (user?.role === 'ADMIN' && usersData?.users) {
        employeeList = usersData.users;
      } else if (['MANAGER', 'RH', 'HOST'].includes(user?.role) && departmentUsersData?.users) {
        employeeList = departmentUsersData.users;
      }
      
      employeeList
        .filter(employee => employee.id !== user?.id && employee.role !== 'VISITOR')
        .forEach(employee => {
          options.push({
            id: employee.id,
            firstName: employee.firstName,
            lastName: employee.lastName,
            department: employee.department,
            label: `${employee.firstName} ${employee.lastName} - ${employee.department?.name || 'Aucun département'}`
          });
        });
    }
    
    return options;
  };

  if (absenceRequestsLoading || absenceTypesLoading || usersLoading || departmentUsersLoading || departmentsLoading || employeePresencesLoading || departmentRequestsLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Gestion des Absences
      </Typography>
      
      <AnimatedPaper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="onglets absences">
          <Tab label="Mes demandes d'absence" />
          {['ADMIN', 'RH', 'HOST'].includes(user?.role) && (
            <Tab label="Demandes du département" />
          )}
        </Tabs>
      </AnimatedPaper>

      {tabValue === 0 && (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
                label="Rechercher"
              variant="outlined"
                size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
              <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="PENDING">En attente</MenuItem>
                  <MenuItem value="APPROVED">Approuvée</MenuItem>
                  <MenuItem value="REJECTED">Rejetée</MenuItem>
                  <MenuItem value="CANCELLED">Annulée</MenuItem>
              </Select>
            </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Type d'absence</InputLabel>
                <Select
                  value={absenceTypeFilter}
                  label="Type d'absence"
                  onChange={(e) => setAbsenceTypeFilter(e.target.value)}
                >
                  <MenuItem value="">Tous</MenuItem>
                  {absenceTypesData?.absenceTypes?.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {(user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'RH') && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Département</InputLabel>
                  <Select
                    value={departmentFilter}
                    label="Département"
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    {departmentsData?.departments?.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            
            {(user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'RH' || user?.role === 'EMPLOYEE' || user?.role === 'HOST') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
                Nouvelle demande
          </Button>
        )}
      </Box>
      
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
                  <TableCell>Employé</TableCell>
                  <TableCell>Type d'absence</TableCell>
                  <TableCell>Période</TableCell>
                  <TableCell>Durée</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
                {filteredRequests.map((request, index) => (
                  <TableRow 
                    key={request.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                      ...tableRowHoverStyles
                    }}
                  >
                <TableCell>
                      {request.employee.firstName} {request.employee.lastName}
                      <br />
                      <Typography variant="caption" color="textSecondary">
                        {request.employee.department?.name || 'Aucun département'}
                      </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                        label={request.absenceType.name} 
                    size="small" 
                        color="primary" 
                        variant="outlined"
                  />
                </TableCell>
                <TableCell>
                      {format(new Date(request.startDate), 'dd/MM/yyyy', { locale: fr })} - {format(new Date(request.endDate), 'dd/MM/yyyy', { locale: fr })}
                    </TableCell>
                    <TableCell>{request.durationDays} jour(s)</TableCell>
                    <TableCell>
                    <Chip 
                        label={getStatusLabel(request.status)} 
                        color={getStatusColor(request.status)} 
                      size="small"
                    />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', '& > *': { display: 'inline-block', marginRight: '4px' } }}>
                      <IconButton onClick={() => handleOpenDetails(request)} color="primary" sx={iconButtonStyles} size="small">
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                      {(user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'RH' || user?.role === 'HOST') && request.status === 'PENDING' && (
                        <IconButton onClick={() => handleOpenValidateDialog(request)} color="success" sx={iconButtonStyles} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      )}
                      {request.status === 'PENDING' && (
                        <IconButton onClick={() => handleOpenDeleteDialog(request)} color="error" sx={iconButtonStyles} size="small">
                        <DeleteIcon fontSize="small" />
                        </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>

          {filteredRequests.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                {searchQuery || statusFilter ? 'Aucune demande trouvée' : 'Aucune demande d\'absence'}
              </Typography>
            </Box>
          )}
        </>
      )}

      {tabValue === 1 && (
        <>
          {/* Filtres pour les demandes du département */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Rechercher"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="PENDING">En attente</MenuItem>
                <MenuItem value="APPROVED">Approuvée</MenuItem>
                <MenuItem value="REJECTED">Rejetée</MenuItem>
                <MenuItem value="CANCELLED">Annulée</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type d'absence</InputLabel>
              <Select
                value={absenceTypeFilter}
                label="Type d'absence"
                onChange={(e) => setAbsenceTypeFilter(e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                {absenceTypesData?.absenceTypes?.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
      </Box>
      
          {/* Tableau des demandes du département */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
                  <TableCell>Employé</TableCell>
                  <TableCell>Type d'absence</TableCell>
                  <TableCell>Période</TableCell>
                  <TableCell>Durée</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
                {filteredRequests.map((request, index) => (
                  <TableRow 
                    key={request.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                      ...tableRowHoverStyles
                    }}
                  >
                <TableCell>
                      {request.employee.firstName} {request.employee.lastName}
                      <br />
                      <Typography variant="caption" color="textSecondary">
                        {request.employee.department?.name || 'Aucun département'}
                      </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                        label={request.absenceType.name} 
                    size="small" 
                        color="primary" 
                        variant="outlined"
                  />
                </TableCell>
                <TableCell>
                      {format(new Date(request.startDate), 'dd/MM/yyyy', { locale: fr })} - {format(new Date(request.endDate), 'dd/MM/yyyy', { locale: fr })}
                    </TableCell>
                    <TableCell>{request.durationDays} jour(s)</TableCell>
                    <TableCell>
                    <Chip 
                        label={getStatusLabel(request.status)} 
                        color={getStatusColor(request.status)} 
                      size="small"
                    />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', '& > *': { display: 'inline-block', marginRight: '4px' } }}>
                      <IconButton onClick={() => handleOpenDetails(request)} color="primary" sx={iconButtonStyles} size="small">
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                      {(user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'RH' || user?.role === 'HOST') && request.status === 'PENDING' && (
                        <IconButton onClick={() => handleOpenValidateDialog(request)} color="success" sx={iconButtonStyles} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      )}
                      {request.status === 'PENDING' && (
                        <IconButton onClick={() => handleOpenDeleteDialog(request)} color="error" sx={iconButtonStyles} size="small">
                        <DeleteIcon fontSize="small" />
                        </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>

          {filteredRequests.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                {searchQuery || statusFilter ? 'Aucune demande trouvée' : 'Aucune demande d\'absence dans le département'}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Dialogue de création */}
      <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <StyledDialogTitle>Nouvelle demande d'absence</StyledDialogTitle>
        <form onSubmit={handleSubmit}>
          <StyledDialogContent>
            <Grid container spacing={2}>
              {/* Sélecteur d'employé pour RH, Admin, Manager, Hôte et Employé */}
              {(user?.role === 'ADMIN' || user?.role === 'RH' || user?.role === 'MANAGER' || user?.role === 'HOST' || user?.role === 'EMPLOYEE') && (
              <Grid item xs={12} md={6}>
                <SearchableSelect
                      label="Employé"
                  value={formData.employeeId || ''}
                  onChange={(value) => {
                    console.log('Changement sélection:', value);
                    setFormData(prev => ({ ...prev, employeeId: value }));
                      }}
                  options={getEmployeeOptionsList()}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  required
                      disabled={usersLoading || departmentUsersLoading}
                  loading={usersLoading || departmentUsersLoading}
                  noOptionsText="Aucun employé disponible"
                />
              </Grid>
              )}
              
              <Grid item xs={12} md={6}>
                <SearchableSelect
                    label="Type d'absence"
                  value={formData.absenceTypeId || ''}
                  onChange={(value) => setFormData({ ...formData, absenceTypeId: value })}
                  options={absenceTypesData?.absenceTypes || []}
                  getOptionLabel={(option) => `${option.name} (${option.code})`}
                  getOptionValue={(option) => option.id}
                  required
                  noOptionsText="Aucun type d'absence disponible"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date de début"
                  type="date"
                  required
                  value={formData.startDate}
                  onChange={(e) => {
                    const startDate = e.target.value;
                    const durationDays = computeDurationDays(startDate, formData.endDate);
                    setFormData({ ...formData, startDate, durationDays });
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date de fin"
                  type="date"
                  required
                  value={formData.endDate}
                  onChange={(e) => {
                    const endDate = e.target.value;
                    const durationDays = computeDurationDays(formData.startDate, endDate);
                    setFormData({ ...formData, endDate, durationDays });
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Durée (jours)"
                  type="number"
                  step="0.5"
                  required
                  value={formData.durationDays}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Motif"
                  multiline
                  rows={3}
                  required
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  accept="image/*,.pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  id="document-upload"
                  type="file"
                  onChange={(e) => setSelectedDocument(e.target.files[0])}
                />
                <label htmlFor="document-upload">
                  <Button variant="outlined" component="span" fullWidth startIcon={<AddIcon />}>
                    {selectedDocument ? selectedDocument.name : 'Ajouter un document (optionnel)'}
                  </Button>
                </label>
              </Grid>
            </Grid>
          </StyledDialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={createLoading || uploadLoading}>
              {createLoading ? <CircularProgress size={20} /> : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialogue de détails */}
      <Dialog open={openDetailsDialog} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <StyledDialogTitle>Détails de la demande d'absence</StyledDialogTitle>
        <StyledDialogContent>
          {selectedRequest && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Employé</Typography>
                <Typography variant="body1">
                  {selectedRequest.employee.firstName} {selectedRequest.employee.lastName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedRequest.employee.email}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Département</Typography>
                <Typography variant="body1">{selectedRequest.employee.department?.name || 'Aucun département'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Type d'absence</Typography>
                <Typography variant="body1">{selectedRequest.absenceType.name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Statut</Typography>
                <Chip 
                  label={getStatusLabel(selectedRequest.status)} 
                  color={getStatusColor(selectedRequest.status)} 
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Période</Typography>
                <Typography variant="body1">
                  {format(new Date(selectedRequest.startDate), 'dd/MM/yyyy', { locale: fr })} - {format(new Date(selectedRequest.endDate), 'dd/MM/yyyy', { locale: fr })}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Durée</Typography>
                <Typography variant="body1">{selectedRequest.durationDays} jour(s)</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Motif</Typography>
                <Typography variant="body1">{selectedRequest.reason}</Typography>
              </Grid>
              {selectedRequest.approvedBy && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Approuvé par</Typography>
                    <Typography variant="body1">
                      {selectedRequest.approvedBy.firstName} {selectedRequest.approvedBy.lastName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Date d'approbation</Typography>
                    <Typography variant="body1">
                      {format(new Date(selectedRequest.approvedAt), 'dd/MM/yyyy HH:mm', { locale: fr })}
                    </Typography>
                  </Grid>
                </>
              )}
              {selectedRequest.rejectionReason && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Motif du rejet</Typography>
                  <Typography variant="body1" color="error">{selectedRequest.rejectionReason}</Typography>
                </Grid>
              )}
              {selectedRequest.documentUrl && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Document</Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    href={selectedRequest.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<VisibilityIcon />}
                  >
                    Voir le document
                  </Button>
                </Grid>
              )}
            </Grid>
          )}
        </StyledDialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de validation */}
      <Dialog open={openValidateDialog} onClose={handleCloseValidateDialog} maxWidth="sm" fullWidth>
        <StyledDialogTitle>Valider la demande d'absence</StyledDialogTitle>
        <StyledDialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Demande de {selectedRequest.employee.firstName} {selectedRequest.employee.lastName}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Demande de {selectedRequest.absenceType.name} - {selectedRequest.durationDays} jour(s)
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Motif:</strong> {selectedRequest.reason}
              </Typography>
              
                <TextField
                  fullWidth
                label="Motif de rejet (si applicable)"
                multiline
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
          </StyledDialogContent>
          <DialogActions>
          <Button onClick={handleCloseValidateDialog}>Annuler</Button>
          <Button 
            onClick={() => handleApproval(false)} 
            color="error" 
            variant="contained"
            disabled={!rejectionReason}
          >
            Rejeter
            </Button>
          <Button 
            onClick={() => handleApproval(true)} 
            color="success" 
            variant="contained"
          >
            Approuver
            </Button>
          </DialogActions>
      </Dialog>

      {/* Dialogue de suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <StyledDialogTitle>Confirmer la suppression</StyledDialogTitle>
        <StyledDialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer cette demande d'absence ?
          </Typography>
          {selectedRequest && selectedRequest.status !== 'PENDING' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Cette demande est {selectedRequest.status === 'APPROVED' ? 'approuvée' : 'rejetée'} et ne peut être supprimée que par un administrateur.
            </Alert>
          )}
        </StyledDialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue d'approbation */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <StyledDialogTitle>
          Approuver ou rejeter la demande
        </StyledDialogTitle>
        <StyledDialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Voulez-vous approuver ou rejeter cette demande d'absence ?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Motif du rejet (optionnel)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            disabled={approvalLoading}
          />
        </StyledDialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)} disabled={approvalLoading}>
            Annuler
          </Button>
          <Button 
            onClick={() => handleApproval(false)} 
            color="error" 
            disabled={approvalLoading}
          >
            Rejeter
          </Button>
          <Button 
            onClick={() => handleApproval(true)} 
            color="success" 
            disabled={approvalLoading}
          >
            Approuver
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Absences; 
